import React from 'react';
import { FormattedMessage, defineMessages } from 'react-intl-next';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { withTransparentErrorBoundary } from '@confluence/error-boundary/entry-points/withErrorBoundary.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';

import { BaseAutomationDiscoveryDialog } from '../BaseAutomationDiscoveryDialog.tsx';
import { EventSourceName } from '../utils/analytics.ts';

export const i18n = defineMessages({
	releaseNotesModalHeader: {
		id: 'automation-discovery.rovo.release-notes.modal.header',
		defaultMessage: '{publishedPageTitle} was successfully published in the {spaceName} space',
		description:
			'This text is displayed as a title in a modal that says the page was published in the specified space',
	},
	releaseNotesCta: {
		id: 'automation-discovery.rovo.release-notes.modal.cta',
		defaultMessage: 'Automate your release notes process?',
		description:
			'This text is displayed in a modal to ask the user if they want to automatically create a release note automation rule',
	},
	releaseNoteKeywordForModalTrigger: {
		id: 'automation-discovery.rovo.release-note.trigger.keyword',
		defaultMessage: 'release note',
		description:
			'We look for this phrase in a Confluence page title in order to determine if an automation modal should be shown',
	},
});

type Intl = {
	formatMessage: (messageDescriptor: { id: string; defaultMessage: string }) => string;
};

export const isEligibleForReleaseNotesDiscovery = (pageTitle: string, intl: Intl): boolean => {
	// show the dialog if title contains "release note"
	return pageTitle
		.toLowerCase()
		.includes(intl.formatMessage(i18n.releaseNoteKeywordForModalTrigger));
};

type ReleaseNotesDialogProps = {
	onClose: () => void;
	onCloseComplete: () => void;
	publishedPageTitle: string;
	spaceName: string;
	spaceKey?: string;
};

const ReleaseNotesDialogComponent = ({
	onClose,
	publishedPageTitle,
	spaceName,
	spaceKey,
}: ReleaseNotesDialogProps) => {
	return (
		<BaseAutomationDiscoveryDialog
			eventSourceName={EventSourceName.RELEASE_NOTES}
			templateName="createReleaseNotesPageAfterJiraIssueCreation"
			discoveryExperience="automationDiscoveryReleaseNotes"
			onClose={onClose}
			cta={<FormattedMessage {...i18n.releaseNotesCta} />}
			header={
				<FormattedMessage
					{...i18n.releaseNotesModalHeader}
					values={{ publishedPageTitle, spaceName }}
				/>
			}
			localStorageKey={keys.AUTOMATION_DISCOVER_RELEASE_NOTES}
			templateUrl={`/wiki/spaces/${spaceKey}/settings/automation#/rule/template?templateId=confluence_rovo_template_4`}
			spaceKey={spaceKey}
		/>
	);
};

export const ReleaseNotesDialog = withTransparentErrorBoundary({
	attribution: Attribution.AUTOMATION_FOR_CONFLUENCE,
})(ReleaseNotesDialogComponent);
